import React from 'react';
import PropTypes from 'prop-types';
import {useMemo, useReducer, useCallback, useEffect} from 'react';

import {AssessmentContext} from './assessment-context';
import axios from 'axios';
import config from '../../helper/config';
import APIHandler from '../../handlers/APIHandler';

const initialState = {
    loading: false, 
    assessment_metadata_initial_loading:true,
    assessment_metadata_loading: false,
    all_assessment_meta_data:{success:false,message:"Initial",data:[]},
    my_assessment_initial_loading:true,
    my_assessment_loading: false,
    my_assessments_data: [],
    public_assessment_initial_loading:true,
    public_assessment_loading: false,
    public_assessments_data: []
}

const reducer = (state, action) => {

  switch (action.type) {

        case 'INITIAL':
            return {
                ...initialState
            }
        case 'SET_LOADING':
            return { 
                ...state, 
                loading: action.payload.loading 
            };
        case 'SET_ASSESSMENT_METADATA_INITIAL_LOADING':
            return { 
                ...state, 
                assessment_metadata_initial_loading: action.payload.assessment_metadata_initial_loading 
            };
        case 'SET_ASSESSMENT_METADATA_LOADING':
            return { 
                ...state, 
                assessment_metadata_loading: action.payload.assessment_metadata_loading 
            };
        case 'SET_ALL_ASSESSMENT_METADATA_SUCCESS':
            return { 
                ...state, 
                all_assessment_meta_data: {
                    success: true,
                    message: action.payload.message || "Successfully fetched assessment metadata",
                    data: action.payload.data || []
                }
            };
        case 'SET_ALL_ASSESSMENT_METADATA_FAILURE':
            return { 
                ...state, 
                all_assessment_meta_data: {
                    success: false,
                    message: action.payload.message || "Failed to fetch assessment metadata",
                    data: []
                }
            };
        case 'SET_MY_ASSESSMENT_INITIAL_LOADING':
            return { 
                ...state, 
                my_assessment_initial_loading: action.payload.my_assessment_initial_loading 
            };
        case 'SET_MY_ASSESSMENT_LOADING':
            return { 
                ...state, 
                my_assessment_loading: action.payload.my_assessment_loading 
            };
        case 'SET_MY_ASSESSMENT':
            return { 
                ...state, 
                my_assessments_data: action.payload.my_assessments_data
            };
        case 'SET_PUBLIC_ASSESSMENT_INITIAL_LOADING':
            return { 
                ...state, 
                public_assessment_initial_loading: action.payload.public_assessment_initial_loading 
            };
        case 'SET_PUBLIC_ASSESSMENT_LOADING':
            return { 
                ...state, 
                public_assessment_loading: action.payload.public_assessment_loading 
            };
        case 'SET_PUBLIC_ASSESSMENT':
            return { 
                ...state, 
                public_assessments_data: action.payload.public_assessments_data
            };
        default:
            return state;
    }
}

export function AssessmentContextProvider({children}) {

    const [state, dispatch] = useReducer(reducer, initialState);

    const initialize = useCallback(async () => {
        
        dispatch({
            type: 'INITIAL',
            payload: {},
        });
        
        }, []);

    useEffect(() => {
        initialize();
    }, [initialize]);

    const fetchAssessmentMetaData = useCallback(
        async () => {
        
        if (state.assessment_metadata_initial_loading) {
            dispatch({ 
                type: 'SET_ASSESSMENT_METADATA_LOADING', 
                payload: { assessment_metadata_loading: true } 
            });
        }
        else{
            dispatch({ 
                type: 'SET_ASSESSMENT_METADATA_LOADING', 
                payload: { assessment_metadata_loading: true } 
            });
        }

        try {
            const url = `${config.oat_api_url}/assessments/get-all-meta-data/`;
            const requestOptions = {
                method: 'GET',
                headers: APIHandler.getHeader(config.azure_ad_config.apis.OAT.name),
            };

            const response = await fetch(url, requestOptions);

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(
                errorData.message || `HTTP error! status: ${response.status}`
                );
            }

            const res_data = await APIHandler.handleResponse(response)
            
            if (res_data.success) {
                dispatch({
                type: 'SET_ALL_ASSESSMENT_METADATA_SUCCESS',
                payload: { 
                    data: res_data.data,
                    message: res_data.message 
                },
                });
            } else {
                dispatch({
                type: 'SET_ALL_ASSESSMENT_METADATA_FAILURE',
                payload: { 
                    message: res_data.message || "Request succeeded but API returned failure"
                },
                });
            }

            if (state.assessment_metadata_initial_loading) {
                dispatch({
                    type: 'SET_ASSESSMENT_METADATA_INITIAL_LOADING',
                    payload: { assessment_metadata_initial_loading: false },
                });
            }
            dispatch({ 
                type: 'SET_ASSESSMENT_METADATA_LOADING', 
                payload: { student_dashboard_data_loading: false } 
            });

        } catch (error) {
            console.error('Error fetching assessment metadata data:', error);
            dispatch({
                type: 'SET_ALL_ASSESSMENT_METADATA_FAILURE',
                payload: { 
                message: error.message || "Network error occurred"
                },
            });
        } finally {
            if (state.assessment_metadata_initial_loading) {
                dispatch({ 
                    type: 'SET_ASSESSMENT_METADATA_LOADING', 
                    payload: { assessment_metadata_loading: false } 
                });
            }
            dispatch({ 
                type: 'SET_ASSESSMENT_METADATA_LOADING', 
                payload: { assessment_metadata_loading: false } 
            });
        }
        },
        [state.assessment_metadata_initial_loading]
    );

    const fetchAllMyAssessments = useCallback(
        async (obj, limit, offset, sort_column, sort_direction, search_term, selectedCategories, selectedAuthors, selectedChapters,
                selectedGrades, selectedDifficulties, selectedLanguages, selectedTerms, selectedYears, selectedsubjects, selectedStatuses, selectedExamCategories, selectedSources, selectedCreators, selectedStartDate, selectedEndDate) => {
        
        if (state.my_assessment_initial_loading) {
            dispatch({ 
                type: 'SET_MY_ASSESSMENT_LOADING', 
                payload: { my_assessment_loading: true } 
            });
        }
        else{
            dispatch({ 
                type: 'SET_MY_ASSESSMENT_LOADING', 
                payload: { my_assessment_loading: true } 
            });
        }

        try {
            const url = `${config.oat_api_url}/assessments/getAllFilteredMyAssessments/managed?limit=${limit}&offset=${offset}&sort_column=${sort_column}&sort_direction=${sort_direction}&search_term=${search_term}&selectedCategories=${selectedCategories}&selectedAuthors=${selectedAuthors}&selectedChapters=${selectedChapters}&selectedGrades=${selectedGrades}&selectedDifficulties=${selectedDifficulties}&selectedLanguages=${selectedLanguages}&selectedTerms=${selectedTerms}&selectedYears=${selectedYears}&selectedsubjects=${selectedsubjects}&selectedStatuses=${selectedStatuses}&selectedExamCategories=${selectedExamCategories}&selectedSources=${selectedSources}&selectedCreators=${selectedCreators}&selectedStartDate=${selectedStartDate}&selectedEndDate=${selectedEndDate}`;
            const requestOptions = {
                method: 'POST',
                headers: APIHandler.getHeader(config.azure_ad_config.apis.OAT.name),
                body: JSON.stringify(obj)
            };

            const response = await fetch(url, requestOptions);

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(
                errorData.message || `HTTP error! status: ${response.status}`
                );
            }

            const res_data = await APIHandler.handleResponse(response)            
            
            dispatch({
                type: 'SET_MY_ASSESSMENT',
                payload: { 
                    my_assessments_data: res_data
                },
            });

            if (state.my_assessment_initial_loading) {
                dispatch({
                    type: 'SET_MY_ASSESSMENT_INITIAL_LOADING',
                    payload: { my_assessment_initial_loading: false },
                });
            }
            dispatch({ 
                type: 'SET_MY_ASSESSMENT_LOADING', 
                payload: { my_assessment_loading: false } 
            });

        } catch (error) {
            console.error('Error fetching my assessment data:', error);
        } finally {
            if (state.my_assessment_initial_loading) {
                dispatch({ 
                    type: 'SET_MY_ASSESSMENT_LOADING', 
                    payload: { my_assessment_loading: false } 
                });
            }
            dispatch({ 
                type: 'SET_MY_ASSESSMENT_LOADING', 
                payload: { my_assessment_loading: false } 
            });
        }
        },
        [state.my_assessment_initial_loading]
    );

    const memoizedValue = useMemo(() => 
        ({
            loading: state.loading,
            assessment_metadata_initial_loading: state.assessment_metadata_initial_loading,
            assessment_metadata_loading: state.assessment_metadata_loading,
            all_assessment_meta_data:state.all_assessment_meta_data,

            my_assessment_initial_loading: state.my_assessment_initial_loading,
            my_assessment_loading: state.my_assessment_loading,
            my_assessments_data: state.my_assessments_data,

            public_assessment_initial_loading: state.public_assessment_initial_loading,
            public_assessment_loading: state.public_assessment_loading,
            public_assessments_data: state.public_assessments_data,
        
            fetchAssessmentMetaData,
            fetchAllMyAssessments,
        }),
        [
            state,
            state.loading,
            state.assessment_metadata_initial_loading,
            state.assessment_metadata_loading,
            state.all_assessment_meta_data,
            state.my_assessment_initial_loading,
            state.my_assessment_loading,
            state.my_assessments_data,
            state.public_assessment_initial_loading,
            state.public_assessment_loading,
            state.public_assessments_data,

            fetchAssessmentMetaData,
            fetchAllMyAssessments
        ]
    );

      return (
        <AssessmentContext.Provider value={memoizedValue}>
          {children}
        </AssessmentContext.Provider>
      );
}

AssessmentContextProvider.propTypes = {
  children: PropTypes.node,
};